import { useState, useCallback } from 'react';
import initializeComponent from 'Shared/react-initialize';
import LightBox, { LightBoxProps, filterIFixitDataPoint } from './lightBox';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { RequiredThemeProps, SchematicThemeProvider } from '../Providers/themes';
import { withApiContext } from '../Providers/apiContext';
import MiscPartThumbnail from '../MiscParts/miscPartSchematic';
import SchematicThumbnail from './schematicThumbnail';
import { DiagramHash, getHashParts } from '../Providers/withRouting';
import { WholeDiagram, DataContextProvider } from 'Guide/Schematics/Diagram/Providers/dataContext';
import { SearchContextProvider } from 'Guide/Schematics/Diagram/Providers/searchContext';
import { KeypressActionProvider } from '../Providers/keypressContext';
import MiscPartsTable from '../MiscParts/miscPartsTable';

// Simple disclosure hook to replace Chakra's useDisclosure
function useDisclosure(options?: { defaultIsOpen?: boolean }) {
   const [isOpen, setIsOpen] = useState(options?.defaultIsOpen ?? false);

   const onOpen = useCallback(() => setIsOpen(true), []);
   const onClose = useCallback(() => setIsOpen(false), []);
   const onToggle = useCallback(() => setIsOpen(prev => !prev), []);

   return { isOpen, onOpen, onClose, onToggle };
}

export interface SchematicProps {
   diagrams: WholeDiagram<iFixitHotSpotItem>[];
   theme: 'blue' | 'orange';
   apiUrl?: string | null;
   apiKey?: string | null;
   newApiKey?: string | null;
   newApiUrl?: string | null;
   createdUserId: string | null;
   squad?: string;
}

type SchematicDiagramProps = SchematicProps['diagrams'][0];

const Schematic = (props: SchematicProps) => {
   const diagrams = props.diagrams.map(diagramProps => {
      const { hashDiagramid, hashSearchText, hashHotspotId } = getHashParts();
      const isWantedDiagram = hashDiagramid === String(diagramProps.diagramid);
      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
      const { isOpen, onOpen, onToggle, onClose } = useDisclosure({
         defaultIsOpen: isWantedDiagram,
      });

      const Component =
         diagramProps.diagramid === 'misc' ? MiscPartDiagramThumbnail : DiagramThumbnail;
      return (
         <Component
            key={diagramProps.diagramid}
            theme={props.theme}
            {...diagramProps}
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            onToggle={onToggle}
            initialSearchText={isWantedDiagram ? hashSearchText : undefined}
            // @ts-expect-error TS(2322) FIXME: Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
            initialHotSpotId={isWantedDiagram ? hashHotspotId : undefined}
         />
      );
   });
   return <>{diagrams}</>;
};

const DiagramThumbnail = ({
   theme,
   thumbnailUrl,
   diagramid,
   title,
   isOpen,
   onOpen,
   onClose,
   initialSearchText,
   initialHotSpotId,
}: {
   thumbnailUrl: SchematicDiagramProps['thumbnailUrl'];
} & LightBoxProps &
   RequiredThemeProps) => (
   <SchematicThemeProvider theme={theme}>
      <SchematicThumbnail thumbnailUrl={thumbnailUrl} title={title} onOpen={onOpen} isOpen={isOpen}>
         <LightBox
            title={title}
            diagramid={diagramid}
            isOpen={isOpen}
            onClose={onClose}
            initialSearchText={initialSearchText}
            initialHotSpotId={initialHotSpotId}
         />
      </SchematicThumbnail>
   </SchematicThemeProvider>
);

export type MiscPartDiagramThumbnailProps = {
   thumbnailUrl: SchematicDiagramProps['thumbnailUrl'];
   data: SchematicDiagramProps['data'];
} & RequiredThemeProps &
   Omit<LightBoxProps, 'imageUrl' | 'width' | 'height'>;

function MiscPartDiagramThumbnail({
   theme,
   data,
   diagramid,
   thumbnailUrl,
   title,
   isOpen,
   onOpen,
   onClose,
   initialSearchText,
}: MiscPartDiagramThumbnailProps) {
   return (
      <SchematicThemeProvider theme={theme}>
         <MiscPartThumbnail
            thumbnailUrl={thumbnailUrl}
            title={title}
            onOpen={onOpen}
            isOpen={isOpen}
         >
            <DataContextProvider data={data}>
               <KeypressActionProvider>
                  <SearchContextProvider
                     initialSearchText={initialSearchText}
                     searchDataPoint={filterIFixitDataPoint}
                  >
                     <DiagramHash diagramid={diagramid} isOpen={isOpen} />
                     <MiscPartsTable title={title} isOpen={isOpen} onClose={onClose} />
                  </SearchContextProvider>
               </KeypressActionProvider>
            </DataContextProvider>
         </MiscPartThumbnail>
      </SchematicThemeProvider>
   );
}

// @ts-expect-error TS(2345) FIXME: Argument of type '(props: SchematicProps) => JSX.E... Remove this comment to see the full error message
initializeComponent('Schematic', withApiContext(Schematic));
